/* eslint-disable no-unused-vars */
import { AccountLogo } from '@frontend/components/common';
import { Application, Functionality } from '@frontend/functionality/types';
import { useMemo } from 'react';

import LicensedNavbarGroupItem from '../licensed-navbar-group-item/licensed-navbar-group-item.component';
import useNavbar from './navbar.controller';

const ID = 'sidenav-main';

export interface NavbarProps {
    className?: string;
}

const Navbar = ({ className }: NavbarProps) => {
    const viewProps = useNavbar();

    // Define the priority order for reserved spots
    const reservedApplications: Application[] = [
        Application.PUDO,
        Application.LAUNDRY,
        Application.LENDING,
        Application.VENDING,
        Application.SHOP,
        Application.NIVALIZ
    ];

    // Separate functionalities into reserved and other applications
    const { reservedFunctionalities, otherFunctionalities } = useMemo(() => {
        if (!viewProps.functionalities) return { reservedFunctionalities: [], otherFunctionalities: [] };

        const reserved: Functionality[][] = [];
        const other: Functionality[][] = [];

        // First, collect reserved functionalities in fixed order
        reservedApplications.forEach((app) => {
            const functionalities = viewProps.functionalities!.filter((l) => l.application === app);
            if (functionalities.length > 0) {
                reserved.push(functionalities);
            }
        });

        // Then, collect other functionalities and sort alphabetically by display name
        const otherFlat = viewProps.functionalities.filter((f) => !reservedApplications.includes(f.application));
        const otherGrouped: { [key: string]: Functionality[] } = {};
        otherFlat.forEach((f) => {
            if (!otherGrouped[f.application]) {
                otherGrouped[f.application] = [];
            }
            otherGrouped[f.application].push(f);
        });
        for (const app in otherGrouped) {
            other.push(otherGrouped[app]);
        }

        // Sort other functionalities alphabetically by application enum value
        other.sort((a, b) => {
            return a[0].application.localeCompare(b[0].application);
        });

        return { reservedFunctionalities: reserved, otherFunctionalities: other };
    }, [viewProps.functionalities]);

    return (
        <div
            className={`h-dvh items-center justify-start ${className ?? ''}`}
            id={ID}>
            <div className='bg-brand-structure w-1.5 self-stretch' />
            <div className='bg-background-surface border-border-default z-50 flex h-full w-72 flex-col items-center justify-between border-r'>
                <div className='flex size-full min-h-0 flex-col items-center justify-start'>
                    <div className='flex w-full items-center justify-center p-10'>
                        <a
                            className={`flex h-14 w-full justify-center`}
                            href='/'>
                            {viewProps.navbarPinned ? (
                                <AccountLogo
                                    key='pinned'
                                    accountId={viewProps.currentAccount}
                                    fallbackSrc={viewProps.brandingLogoExpanded}
                                />
                            ) : (
                                <AccountLogo
                                    key='unpinned'
                                    accountId={viewProps.currentAccount}
                                    fallbackSrc={viewProps.brandingLogoCollapsed}
                                />
                            )}
                        </a>
                    </div>
                    <div className='border-border-default flex w-full flex-col items-center justify-center gap-2 border-t p-4'>
                        {viewProps.functionalities &&
                            reservedFunctionalities.map((functionalities) => {
                                return (
                                    <LicensedNavbarGroupItem
                                        navbarRef={viewProps.navbarRef}
                                        key={functionalities[0].application}
                                        functionalities={functionalities!}
                                        showIcon={true}
                                    />
                                );
                            })}
                    </div>
                    <div className='border-border-default flex min-h-0 w-full flex-1 flex-col items-center justify-start gap-2 overflow-y-auto border-t p-4'>
                        {viewProps.functionalities &&
                            otherFunctionalities.map((functionalities) => {
                                return (
                                    <LicensedNavbarGroupItem
                                        navbarRef={viewProps.navbarRef}
                                        key={functionalities[0].application}
                                        functionalities={functionalities!}
                                    />
                                );
                            })}
                    </div>
                </div>
            </div>
        </div>
    );
};

export enum NavbarItems {
    PROFILE = 'profile',
    INVENTORY = 'inventory',
    LOCATIONS = 'locations',
    ASSETS = 'assets',
    PUDO = 'pudo',
    PRODUCTS = 'products',
    CATALOGUES = 'catalogues',
    CONTACTS = 'contacts',
    ADDRESS_BOOK = 'address-book',
    SPOTS = 'spots',
    SHARED_SPOTS = 'shared-spots',
    REPORTING = 'reporting',
    COST_CENTERS = 'cost-centers',
    MONITORING = 'monitoring',
    HISTORY = 'history',
    STATISTICS = 'statistics'
}

export default Navbar;
